import React from "react";
import { Link } from "react-router-dom";
import {
  Music, Sparkles, ArrowRight, Activity, Camera,
} from "lucide-react";
import BrandMark from "../components/BrandMark";
import BentoSection from "./landing/BentoSection";
import HeroBackdrop from "./landing/HeroBackdrop";
import HowItWorksSection from "./landing/HowItWorksSection";
import FinalCtaSection from "./landing/FinalCtaSection";
import LandingFooter from "./landing/LandingFooter";
import TickerMarquee from "./landing/TickerMarquee";

/* ============================================================
   Landing — DAIONE (dark-only, redesigned Feb 2026)
   ============================================================
   - Single immersive opening (merged DAIONE banner + headline)
   - Sleek nav with subtle hairline border, no theme switcher
   - Infinite marquee ticker replaces centered trust strip
   - Inline mini-stat strip embedded in the hero footer
   - Compact, asymmetric hero CTAs + supporting micro-copy
*/
export default function Landing() {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="landing-root">
      {/* ============== Nav ============== */}
      <nav
        className="fixed top-0 inset-x-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/[0.06]"
        data-testid="landing-nav"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="brand-logo">
            <BrandMark size={30} />
          </Link>
          <div className="hidden md:flex items-center gap-9 text-sm text-white/60">
            <a href="#features" className="hover:text-white transition-colors" data-testid="nav-features">Features</a>
            <a href="#how" className="hover:text-white transition-colors" data-testid="nav-how">How it works</a>
            <Link to="/pricing" className="hover:text-white transition-colors" data-testid="nav-pricing">Pricing</Link>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/auth"
              className="inline-flex items-center text-sm text-white/70 hover:text-white px-3 sm:px-4 py-2 transition-colors"
              data-testid="nav-login-link"
            >
              Sign in
            </Link>
            <Link
              to="/auth?mode=signup"
              className="group inline-flex items-center gap-2 bg-white text-black font-semibold text-sm pl-4 pr-3.5 py-2 rounded-full hover:gap-3 transition-all"
              data-testid="nav-cta-button"
            >
              Get started
              <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>
      </nav>

      {/* ============== HERO — single immersive scene ============== */}
      <section
        className="relative isolate overflow-hidden border-b border-white/[0.06]"
        data-testid="hero-section"
        style={{ minHeight: "100vh" }}
      >
        {/* Cinematic photo carousel + Ken Burns */}
        <HeroBackdrop />
        {/* Layered washes for depth */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/85 to-transparent" aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-transparent to-transparent opacity-70" aria-hidden="true" />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 70% 50% at 20% 100%, rgba(107,155,255,0.18), transparent 60%)",
          }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 grid-bg opacity-[0.08]" aria-hidden="true" />

        {/* Content */}
        <div className="relative max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 pt-32 pb-20 sm:pt-40 sm:pb-24 lg:pt-44 lg:pb-28 flex flex-col min-h-screen">
          {/* Tag pill */}
          <div className="fade-up inline-flex items-center gap-2 self-start border border-white/15 bg-white/[0.04] backdrop-blur px-3 py-1.5 rounded-full mb-8">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inset-0 rounded-full bg-[#6B9BFF] animate-ping opacity-70" />
              <span className="relative rounded-full h-1.5 w-1.5 bg-[#6B9BFF]" />
            </span>
            <span className="text-[11px] uppercase tracking-[0.18em] text-white/75 font-medium">
              Now powered by Claude Sonnet 4.5
            </span>
          </div>

          {/* Wordmark + headline composition */}
          <div className="flex-1 flex flex-col justify-center">
            <img
              src="/daione-banner.png"
              alt="DAIONE"
              draggable="false"
              className="daione-rise select-none block w-[min(72vw,460px)] h-auto mb-10 sm:mb-12"
              data-testid="brand-banner-image"
              style={{ animationDelay: "0.05s" }}
            />

            <h1
              className="font-display leading-[0.86] tracking-tight max-w-5xl text-5xl sm:text-7xl lg:text-[7.2rem] mb-7"
              data-testid="hero-headline"
            >
              {[
                { w: "Workouts.", className: "" },
                { w: "Food.",     className: "", mr: "0" },
                { br: true },
                { w: "Body.",     className: "" },
                { w: "Music.",    className: "text-[#6B9BFF]" },
                { br: true },
                { w: "One",       className: "text-white/55" },
                { w: "app.",      className: "text-white/55", mr: "0" },
              ].map((tok, i) =>
                tok.br ? (
                  <br key={`br-${i}`} />
                ) : (
                  <span
                    key={`${tok.w}-${i}`}
                    className="hero-word"
                    style={{
                      animationDelay: `${i * 90 + 250}ms`,
                      marginRight: tok.mr ?? "0.22em",
                    }}
                  >
                    <span className={tok.className}>{tok.w}</span>
                  </span>
                )
              )}
            </h1>

            <p className="fade-up text-base sm:text-lg text-white/65 max-w-xl mb-10 leading-relaxed" style={{ animationDelay: "1.2s" }}>
              Your weekly training, every calorie from a photo, body measurements
              and the right soundtrack. One AI-driven control panel.
            </p>

            <div className="fade-up flex flex-wrap items-center gap-3" style={{ animationDelay: "1.4s" }}>
              <Link
                to="/auth?mode=signup"
                className="group inline-flex items-center gap-2 bg-white text-black font-bold text-sm uppercase tracking-widest px-6 py-3.5 rounded-full hover:gap-3 transition-all"
                data-testid="hero-cta-button"
              >
                Start free
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
              <a
                href="#features"
                className="inline-flex items-center gap-2 border border-white/20 hover:border-white/45 text-white/85 hover:text-white font-bold text-sm uppercase tracking-widest px-6 py-3.5 rounded-full transition-colors"
                data-testid="hero-secondary-button"
              >
                Explore the app
              </a>
            </div>
          </div>

          {/* Inline mini-stat strip */}
          <div className="fade-up grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/[0.06] border border-white/10 rounded-2xl overflow-hidden mt-16 max-w-3xl" style={{ animationDelay: "1.6s" }}>
            {[
              { v: "156", l: "Exercises" },
              { v: "12wk", l: "AI programs" },
              { v: "24/7", l: "Coach access" },
              { v: "60s", l: "To first plan" },
            ].map((s) => (
              <div key={s.l} className="bg-[#0a0a0a]/85 backdrop-blur-sm px-5 py-4">
                <div className="font-display text-2xl sm:text-3xl text-white leading-none">{s.v}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-2">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== Marquee ticker ============== */}
      <TickerMarquee />

      {/* ============== Sections ============== */}
      <BentoSection />
      <HowItWorksSection />
      <FinalCtaSection />
      <LandingFooter />
    </div>
  );
}
